import { flatten } from 'utils';
import messagesInCanadianFrench from 'lang/fr-CA.json';
import messagesInDutch from 'lang/nl-NL.json';
import messagesInEnglish from 'lang/en.json';
import messagesInFrench from 'lang/fr-FR.json';
import messagesInGerman from 'lang/de-DE.json';
import messagesInSpanish from 'lang/es-ES.json';
import messagesInItalian from 'lang/it-IT.json';
import messagesInNorwegian from 'lang/nb-NO.json';
import messagesInPolish from 'lang/pl.json';
import messagesInPortuguese from 'lang/pt-PT.json';
import messagesInHungarian from 'lang/hu-HU.json';
import messagesInSlovak from 'lang/sk-SK.json';
import messagesInUnitedKingdomEnglish from 'lang/en-GB.json';
import messagesInCanadianEnglish from 'lang/en-CA.json';
import messagesInIrishEnglish from 'lang/en-IE.json';
import messagesInAustralianEnglish from 'lang/en-AU.json';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

// Keyed by BOTH the regional tag and the bare language. Chrome sends a bare `de` for a German user
// with no region set, and a region we do not ship (de-AT, fr-BE, es-MX, nl-BE) splits to one — without
// the bare key both fall through to English with the catalogue sitting right here. `no` is the
// macrolanguage tag some hosts send for Norwegian.
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.payments],
  ['es-es', messagesInSpanish.payments],
  ['es', messagesInSpanish.payments],
  ['fr-fr', messagesInFrench.payments],
  ['fr', messagesInFrench.payments],
  ['fr-ca', messagesInCanadianFrench.payments],
  ['de-de', messagesInGerman.payments],
  ['de', messagesInGerman.payments],
  ['nl-nl', messagesInDutch.payments],
  ['nl', messagesInDutch.payments],
  ['it-it', messagesInItalian.payments],
  ['it', messagesInItalian.payments],
  ['nb-no', messagesInNorwegian.payments],
  ['nb', messagesInNorwegian.payments],
  ['no', messagesInNorwegian.payments],
  ['pl', messagesInPolish.payments],
  ['pt-pt', messagesInPortuguese.payments],
  ['pt', messagesInPortuguese.payments],
  ['hu-hu', messagesInHungarian.payments],
  ['hu', messagesInHungarian.payments],
  ['sk-sk', messagesInSlovak.payments],
  ['sk', messagesInSlovak.payments],
  ['en-gb', messagesInUnitedKingdomEnglish.payments],
  ['en-ca', messagesInCanadianEnglish.payments],
  ['en-ie', messagesInIrishEnglish.payments],
  ['en-au', messagesInAustralianEnglish.payments],
]);

export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Normalize case + separator before the lookup: the map keys are lower-case hyphenated, but a host
  // may pass a canonical ('hu-HU', region-capitalised by Intl) or an underscore ('hu_HU') tag.
  // Matching the raw value dropped every such tag to English.
  const normalized = userLocale.toLowerCase().replace(/_/g, '-');

  if (messagesMap.has(normalized)) {
    return [normalized, normalized];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, normalized];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => {
  return useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);

    return {
      locale,
      messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
    };
  }, [userLocale]);
};
